import { Entity } from '../core/entity.js';
import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { Tags } from './tags.js';
import { drawVehicle } from '../render/vehicle_art.js';
import {
  VEHICLE_DEFS, VEHICLE_SIZE, STAGE_COUNT, EVENT_EVERY_TILES, rollRoadEvent,
  REPAIR_COST, REPAIR_GAIN, FUEL_PER_CAN, PUSH_SPEED,
} from '../data/vehicles.js';

/**
 * 开车。
 *
 * 三条规矩：
 *   1. 车是「一行数据库 + 一个实体」，走到哪个场景就出现在哪个场景；
 *   2. 上车后 movement / pose 系统一律让路，车自己算转向、油门和碰撞；
 *   3. 油要烧、车要坏、坏透了只能推着走——这是它逼人落地的方式。
 *
 * 操作：靠近车按 F 上车 / 下车（触屏有同名按钮），
 *       W 油门、S 刹车（停下再按是倒车）、A/D 转向、Shift 提速、Space 手刹。
 */

const TS = 40;                 // 一格像素，场景里没给就看这个数
const MOUNT_DIST = 62;         // 多远能上车
const HIT_COOLDOWN = 0.45;     // 撞一下多久之后再算账
const HIT_MIN_SPEED = 110;     // 慢到这个速度以下，蹭一下不算数

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

function approach(cur, target, step) {
  if (cur < target) return Math.min(cur + step, target);
  return Math.max(cur - step, target);
}

/** 角度归一到 [-PI, PI] */
function wrapAngle(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

function tileSize(scene) {
  return (scene && scene.tilemap && scene.tilemap.tileSize) || TS;
}

/** 车的碰撞盒：比人宽一圈，小巷子进不去 */
function blockedWide(scene, x, y) {
  const tsNow = tileSize(scene);
  const tm = scene.tilemap;
  const hw = VEHICLE_SIZE.w / 2;
  const hh = VEHICLE_SIZE.h;
  const pts = [
    [x - hw, y - hh], [x + hw, y - hh],
    [x - hw, y], [x + hw, y],
    [x, y - hh / 2],
  ];
  if (tm) {
    for (const [px, py] of pts) if (tm.isSolidAtPixel(px, py)) return true;
  }
  for (const p of scene.props) {
    if (!p.solid) continue;
    const px = p.gx * tsNow, py = p.gy * tsNow;
    const pw = p.w * tsNow, ph = p.h * tsNow;
    const solidTop = py + ph * 0.35;
    if (x + hw > px + 1 && x - hw < px + pw - 1 && y > solidTop && y - hh < py + ph) return true;
  }
  return false;
}

function rowOf(db, rowId) {
  return db.table('vehicles').find(rowId) || null;
}

export const Vehicle = {
  /** 给仪表盘看的一张快照，不在车上就是 null */
  state: null,

  /* ------------------------------ 查 ------------------------------ */

  rows(db) { return db.table('vehicles').all(); },

  carIn(db, sceneId) {
    return this.rows(db).filter((r) => r.scene === sceneId && Number(r.stage) >= STAGE_COUNT);
  },

  firstCar(db) {
    return this.rows(db).filter((r) => Number(r.stage) >= STAGE_COUNT)[0] || null;
  },

  def(row) { return VEHICLE_DEFS[row && row.kind] || VEHICLE_DEFS.wreck; },

  isDriving() { return !!(this.state && this.state.driving); },

  /* ------------------------------ 系统性外部动作 ------------------------------ */

  /**
   * 加油：一壶灯油 FUEL_PER_CAN 点油。amount 缺省就是加到满。
   * 油箱是车自带的，不用往背包里倒。
   */
  refuel(db, amount = 0) {
    const row = this.firstCar(db);
    if (!row) return { ok: false, text: '车还没有影呢，先把车攒出来。' };
    const def = this.def(row);
    const maxF = Number(row.maxFuel) || def.maxFuel;
    const room = Math.max(0, maxF - Number(row.fuel || 0));
    if (room <= 0) return { ok: false, text: `${row.name}的油箱是满的。` };
    const cans = Repo.countItem(db, 'fuel');
    if (cans <= 0) return { ok: false, text: '身上没有灯油。' };

    let want = amount > 0 ? Math.min(amount, room) : room;
    const used = Math.min(cans, Math.ceil(want / FUEL_PER_CAN));
    Repo.takeItem(db, 'fuel', used);
    const add = Math.min(room, used * FUEL_PER_CAN);
    db.table('vehicles').update(row.id, { fuel: Number(row.fuel || 0) + add });
    return { ok: true, text: `加了${used}壶灯油，${row.name}的油表动了${add}格。` };
  },

  /** 就地修：铁片加螺栓，回一截车况 */
  repair(db) {
    const row = this.firstCar(db);
    if (!row) return { ok: false, text: '车还没有影呢，先把车攒出来。' };
    const lack = Object.entries(REPAIR_COST).filter(([id, n]) => Repo.countItem(db, id) < n);
    if (lack.length) {
      return { ok: false, text: `缺${lack.map(([id]) => id === 'scrap_iron' ? '废铁' : '螺栓').join('、')}，修不成。` };
    }
    const dur = Number(row.durability || 0);
    const maxD = Number(row.maxDur) || this.def(row).maxDur;
    if (dur >= maxD) return { ok: false, text: '车况还扎实，用不着修。' };
    for (const [id, n] of Object.entries(REPAIR_COST)) Repo.takeItem(db, id, n);
    const next = Math.min(maxD, dur + REPAIR_GAIN);
    db.table('vehicles').update(row.id, { durability: next, broken: false });
    Tags.gain(db, 'machinery', 2, 'repair');
    return { ok: true, text: `抡了一遍扳手，车况回到${Math.round(next / maxD * 100)}%。` };
  },

  /** 升档：材料和钱都够，就把这辆车改造成下一档 */
  upgrade(db) {
    const row = this.firstCar(db);
    if (!row) return { ok: false, text: '车还没有影呢，先把车攒出来。' };
    const up = this.def(row).upgrade;
    if (!up) return { ok: false, text: `${row.name}已经到顶了。` };
    const p = Repo.getPlayer(db);
    const lack = Object.entries(up.need).filter(([id, n]) => Repo.countItem(db, id) < n);
    if (lack.length) return { ok: false, text: `缺料：${lack.map(([id, n]) => `${id}×${n}`).join('、')}` };
    if ((p.cash || 0) < up.cash) return { ok: false, text: `还得${up.cash}块钱手工钱。` };

    for (const [id, n] of Object.entries(up.need)) Repo.takeItem(db, id, n);
    Repo.savePlayer(db, { cash: (p.cash || 0) - up.cash });
    const ndef = VEHICLE_DEFS[up.to];
    db.table('vehicles').update(row.id, {
      kind: up.to, name: ndef.name,
      maxFuel: ndef.maxFuel, maxDur: ndef.maxDur,
      fuel: Math.min(Number(row.fuel || 0), ndef.maxFuel),
      durability: Math.min(Number(row.durability || 0), ndef.maxDur),
    });
    Tags.gain(db, 'machinery', 6, 'assemble');
    return { ok: true, text: `${row.name}折腾成了${ndef.name}。` };
  },

  /* ------------------------------ 每帧 ------------------------------ */

  updateSystem(dt, ctx) {
    const { scene, db, input } = ctx;
    if (!scene) return;
    const p = scene.first('transform', 'player');
    if (!p) return;

    this.syncEntities(ctx);

    if (p.veh) this.drive(dt, ctx, p);
    else this.hoverHint(ctx, p);

    if (input && input.justPressed('r')) this.toggleMount(ctx, p);
  },

  /** 本场景里该出现的车，建成 entity；不属于这场子的收起来 */
  syncEntities(ctx) {
    const { scene, db } = ctx;
    if (!scene._vehEnts) scene._vehEnts = new Map();
    const tsNow = tileSize(scene);
    const here = this.carIn(db, scene.id);
    const seen = new Set();

    for (const row of here) {
      seen.add(row.id);
      let e = scene._vehEnts.get(row.id);
      if (!e || e.dead) {
        e = new Entity({
          name: row.name || '车',
          transform: {
            x: Number(row.gx) * tsNow + tsNow / 2,
            y: Number(row.gy) * tsNow + tsNow / 2,
            w: VEHICLE_SIZE.w, h: VEHICLE_SIZE.h,
          },
          vehicle: { rowId: row.id, kind: row.kind, angle: Number(row.angle) || 0, broken: !!row.broken },
          drawable: {
            draw: (c, ent, view) => drawVehicle(c, ent.transform.x, ent.transform.y, {
              kind: ent.vehicle.kind,
              angle: ent.vehicle.angle,
              broken: ent.vehicle.broken,
              time: view.time,
            }),
          },
        });
        scene.add(e);
        scene._vehEnts.set(row.id, e);
      } else {
        e.vehicle.kind = row.kind;
        e.vehicle.broken = !!row.broken;
      }
    }

    for (const [id, e] of [...scene._vehEnts]) {
      if (!seen.has(id)) { scene.remove(e); scene._vehEnts.delete(id); }
    }
  },

  /** 站在车边上给个提示（借用交互气泡那一套） */
  hoverHint(ctx, p) {
    const { scene, engine } = ctx;
    const near = this.nearestCarEntity(scene, p);
    const v = engine && engine.renderer ? engine.renderer.view : null;
    if (!near || !v) return;
    if (v.focus) return;                    // 已经有别的提示，不抢
    const broken = !!near.row.broken;
    v.focus = {
      cx: near.ent.transform.x,
      cy: near.ent.transform.y - 18,
      label: near.row.name || '车',
      hint: broken ? 'R 推着走' : 'R 上车',
    };
  },

  nearestCarEntity(scene, p, maxDist = MOUNT_DIST) {
    if (!scene._vehEnts) return null;
    const px = p.transform.x, py = p.transform.y;
    let best = null, bestD = maxDist;
    for (const [rowId, ent] of scene._vehEnts) {
      const row = this.rowFor(scene, rowId);
      if (!row) continue;
      const d = Math.hypot(ent.transform.x - px, ent.transform.y - py);
      if (d < bestD) { bestD = d; best = { ent, row }; }
    }
    return best;
  },

  rowFor(scene, rowId) {
    const db = this._db;
    return db ? rowOf(db, rowId) : null;
  },

  /** F：上车 / 下车 */
  toggleMount(ctx, p) {
    if (p.veh) { this.unmount(ctx, p); return; }
    const near = this.nearestCarEntity(ctx.scene, p);
    if (!near) return;
    this.mount(ctx, p, near);
  },

  mount(ctx, p, near) {
    const { scene, engine } = ctx;
    const { ent, row } = near;
    const def = this.def(row);

    scene.remove(ent);
    scene._vehEnts.delete(row.id);

    p._oldDraw = p.drawable ? p.drawable.draw : null;
    p.transform.x = ent.transform.x;
    p.transform.y = ent.transform.y;
    p.veh = {
      rowId: row.id, kind: row.kind, name: row.name || def.name,
      angle: ent.vehicle.angle || 0,
      speed: 0,
      fuel: Number(row.fuel || 0),
      maxFuel: Number(row.maxFuel) || def.maxFuel,
      dur: Number(row.durability || 0),
      maxDur: Number(row.maxDur) || def.maxDur,
      broken: !!row.broken,
      odo: Number(row.odo || 0),
      _eventAcc: 0,
      _syncAcc: 0,
      _hitCd: 0,
      _outOfFuelSaid: false,
    };
    if (p.drawable) {
      p.drawable.draw = (c, e, view) => drawVehicle(c, e.transform.x, e.transform.y, {
        kind: e.veh.kind,
        angle: e.veh.angle,
        broken: e.veh.broken,
        moving: Math.abs(e.veh.speed) > 20,
        speed: e.veh.speed,
        time: view.time,
      });
    }
    if (engine) engine.events.emit(EV.TOAST, {
      text: p.veh.broken ? `${p.veh.name}瘫着，只能推着走。` : `坐进${p.veh.name}。W 油门，A/D 转向，空格手刹。`,
      kind: 'info',
    });
  },

  unmount(ctx, p) {
    const { scene, engine } = ctx;
    const veh = p.veh;
    if (!veh) return;
    const tsNow = tileSize(scene);
    const spot = this.freeSpot(scene, p.transform.x, p.transform.y);
    p.transform.x = spot.x;
    p.transform.y = spot.y;
    if (p.drawable && p._oldDraw) p.drawable.draw = p._oldDraw;
    p._oldDraw = null;
    p.veh = null;
    this.state = null;

    const tbl = this._db.table('vehicles');
    tbl.update(veh.rowId, {
      scene: scene.id,
      gx: Math.floor(spot.x / tsNow),
      gy: Math.floor(spot.y / tsNow),
      fuel: Math.max(0, veh.fuel),
      durability: Math.max(0, veh.dur),
      maxFuel: veh.maxFuel,
      maxDur: veh.maxDur,
      angle: veh.angle,
      broken: veh.broken,
      odo: veh.odo,
    });
    if (engine) engine.events.emit(EV.TOAST, { text: '把车停稳，下来了。', kind: 'info' });
  },

  /** 下车站在哪一格：车身周围绕一圈找能站人的地方 */
  freeSpot(scene, x, y) {
    const tsNow = tileSize(scene);
    const offsets = [[38, 0], [-38, 0], [0, 32], [0, -32], [30, 26], [-30, 26], [30, -26], [-30, -26]];
    for (const [dx, dy] of offsets) {
      const nx = x + dx, ny = y + dy;
      if (!blockedWide(scene, nx, ny)) return { x: nx, y: ny };
    }
    return { x: x + tsNow, y };
  },

  drive(dt, ctx, p) {
    const { scene, input, engine, events } = ctx;
    const veh = p.veh;
    const def = this.def({ kind: veh.kind });
    const tsNow = tileSize(scene);

    veh._hitCd = Math.max(0, (veh._hitCd || 0) - dt);

    /* ---- 读操作：键盘一套，摇杆一套 ---- */
    let throttle = 0, steer = 0, brake = false;
    const boost = input.isDown('Shift');
    const hand = input.isDown(' ') || input.isDown('Space');
    const tAxis = input.touchAxis;

    if (tAxis && (tAxis.x !== 0 || tAxis.y !== 0)) {
      const len = Math.min(1, Math.hypot(tAxis.x, tAxis.y));
      const want = Math.atan2(tAxis.y, tAxis.x);
      const diff = wrapAngle(want - veh.angle);
      steer = clamp(diff / 0.9, -1, 1);
      throttle = len > 0.35 ? len : 0;
    } else {
      const up = input.isDown('w') || input.isDown('ArrowUp');
      const down = input.isDown('s') || input.isDown('ArrowDown');
      const left = input.isDown('a') || input.isDown('ArrowLeft');
      const right = input.isDown('d') || input.isDown('ArrowRight');
      if (up) throttle = 1;
      if (down) {
        if (veh.speed > 8) brake = true;
        else throttle = -1;
      }
      if (left) steer -= 1;
      if (right) steer += 1;
    }

    /* ---- 有油有力才能跑：没油、瘫了都只剩推 ---- */
    const noGo = veh.broken || veh.fuel <= 0;
    if (noGo) {
      if (veh.fuel <= 0 && !veh._outOfFuelSaid) {
        veh._outOfFuelSaid = true;
        events.emit(EV.TOAST, { text: '油箱见底，只能推着走了。', kind: 'warn' });
      }
      throttle = Math.min(throttle, 0) + 0;
      if (throttle > 0) throttle = 0;
    }

    /* ---- 转向：车不动方向盘打死也没用 ---- */
    const grip = clamp(Math.abs(veh.speed) / 70, 0, 1);
    veh.angle += steer * def.handling * grip * dt * (veh.speed < -5 ? -1 : 1);
    veh.angle = wrapAngle(veh.angle);

    /* ---- 油门 / 刹车 / 自然减速 ---- */
    const topSpeed = noGo ? PUSH_SPEED : (boost ? def.boostSpeed : def.maxSpeed);
    if (hand) {
      veh.speed = approach(veh.speed, 0, def.brake * 1.4 * dt);
    } else if (brake) {
      veh.speed = approach(veh.speed, 0, def.brake * dt);
    } else if (throttle > 0) {
      veh.speed += def.accel * throttle * dt;
    } else if (throttle < 0) {
      veh.speed -= def.accel * 0.45 * dt;
    } else {
      veh.speed = approach(veh.speed, 0, def.accel * 0.55 * dt);
    }
    // 车况差，跑不快
    const durK = 0.55 + 0.45 * clamp(veh.dur / Math.max(1, veh.maxDur), 0, 1);
    veh.speed = clamp(veh.speed, -topSpeed * 0.5, topSpeed * durK);

    /* ---- 动起来：先整段走，撞了就分轴试 ---- */
    const dx = Math.cos(veh.angle) * veh.speed * dt;
    const dy = Math.sin(veh.angle) * veh.speed * dt;
    const nx = p.transform.x + dx;
    const ny = p.transform.y + dy;
    const stuckHere = blockedWide(scene, p.transform.x, p.transform.y);

    if (!blockedWide(scene, nx, ny) || stuckHere) {
      p.transform.x = nx;
      p.transform.y = ny;
    } else {
      let moved = false;
      if (!blockedWide(scene, nx, p.transform.y)) { p.transform.x = nx; moved = true; }
      else if (!blockedWide(scene, p.transform.x, ny)) { p.transform.y = ny; moved = true; }
      if (!moved) {
        const hard = Math.abs(veh.speed) > HIT_MIN_SPEED;
        if (hard && (veh._hitCd || 0) <= 0) {
          veh._hitCd = HIT_COOLDOWN;
          veh.dur = Math.max(0, veh.dur - 1);
          veh.speed = -veh.speed * 0.18;
          events.emit(EV.TOAST, { text: '磕了一记，车皮掉一块。', kind: 'warn' });
        } else {
          veh.speed = 0;
        }
      }
    }

    /* ---- 烧油、磨车：一格一格算 ---- */
    const tiles = Math.hypot(dx, dy) / tsNow;
    if (tiles > 0 && !noGo) {
      const rate = def.fuelRate * (boost ? 1.6 : 1) * (hand ? 0.2 : 1);
      veh.fuel = Math.max(0, veh.fuel - tiles * rate);
      veh.odo += tiles;
      veh._eventAcc = (veh._eventAcc || 0) + tiles;

      if (veh._eventAcc >= EVENT_EVERY_TILES) {
        veh._eventAcc = 0;
        this.roadEvent(ctx, p, veh);
      }
      // 车况见底的临界提示
      if (veh.dur <= 0 && !veh.broken) {
        veh.broken = true;
        events.emit(EV.TOAST, {
          text: `${veh.name}终于趴窝了。铁片加螺栓能就地修，或者推着走。`,
          kind: 'warn',
        });
      }
    }

    /* ---- 每半秒落一次库，掉线也不丢车 ---- */
    veh._syncAcc = (veh._syncAcc || 0) + dt;
    if (veh._syncAcc > 0.5) {
      veh._syncAcc = 0;
      this._db.table('vehicles').update(veh.rowId, {
        scene: scene.id,
        gx: Math.floor(p.transform.x / tsNow),
        gy: Math.floor(p.transform.y / tsNow),
        fuel: Math.max(0, veh.fuel),
        durability: Math.max(0, veh.dur),
        maxFuel: veh.maxFuel, maxDur: veh.maxDur,
        angle: veh.angle, broken: veh.broken, odo: veh.odo,
      });
    }

    /* ---- 给仪表盘 ---- */
    this.state = {
      driving: true,
      name: veh.name, kind: veh.kind,
      speed: Math.abs(veh.speed),
      fuel: veh.fuel, maxFuel: veh.maxFuel,
      dur: veh.dur, maxDur: veh.maxDur,
      broken: veh.broken, outOfFuel: veh.fuel <= 0,
      odo: veh.odo,
    };
  },

  /** 每 EVENT_EVERY_TILES 格掷一次：路不会一直顺 */
  roadEvent(ctx, p, veh) {
    const { events, db } = ctx;
    const ev = rollRoadEvent();
    if (!ev || !ev.effect) {
      if (ev && ev.id !== 'nothing') events.emit(EV.TOAST, { text: ev.text, kind: 'info' });
      return;
    }
    const ef = ev.effect;
    if (ef.durability) {
      veh.dur = clamp(veh.dur + ef.durability, 0, veh.maxDur);
      if (veh.dur <= 0) veh.broken = true;
    }
    if (ef.fuel) veh.fuel = Math.max(0, veh.fuel + ef.fuel);
    if (ef.speedKick) veh.speed *= ef.speedKick;
    if (ef.cash) {
      const lo = ef.cash[0], hi = ef.cash[1];
      const got = lo + Math.floor(Math.random() * (hi - lo + 1));
      const pl = Repo.getPlayer(db);
      Repo.savePlayer(db, { cash: (pl.cash || 0) + got });
    }
    if (ev.text) events.emit(EV.TOAST, { text: ev.text, kind: 'info' });
  },
};

export const vehicleSystem = {
  name: 'vehicle',
  update(dt, ctx) {
    Vehicle._db = ctx.db;
    Vehicle.updateSystem(dt, ctx);
  },
};
